"use client";

import { useEffect, useState } from "react";

export type Lang = "mn" | "en" | "zh";

export const LANGS: { code: Lang; label: string; flag: string }[] = [
  { code: "en", label: "English", flag: "/flags/gb.svg" },
  { code: "mn", label: "Монгол", flag: "/flags/mn.svg" },
  { code: "zh", label: "中文", flag: "/flags/cn.svg" },
];

export interface Dict {
  assistantSubtitle: string;
  signOut: string;
  greeting: string;
  greetingSub: string;
  placeholder: string;
  thinking: string;
  working: string;
  searching: string;
  errorPrefix: string;
  networkError: string;
  notifOn: string;
  notifOff: string;
  notifEnable: string;
  backToTeam: string;
  directChat: string;
  suggestions: string[];
  navFeatures: string;
  navHow: string;
  navContact: string;
  liveDemo: string;
  signIn: string;
  heroBadge: string;
  heroTitlePre: string;
  heroTitleAccent: string;
  heroSub: string;
  heroCtaLogin: string;
  heroCtaDemo: string;
}

export const DICT: Record<Lang, Dict> = {
  mn: {
    // chat
    assistantSubtitle: "Agentic Force туслах",
    signOut: "Гарах",
    greeting: "Сайн байна уу",
    greetingSub: "Би Саруул — даалгавраа хэлээрэй, баг хамт ажиллана.",
    placeholder: "Саруулд даалгавар бичих…",
    thinking: "Саруул бодож байна…",
    working: "Баг ажиллаж байна…",
    searching: "хайж байна",
    errorPrefix: "Алдаа",
    networkError: "Сүлжээний алдаа.",
    notifOn: "Мэдэгдэл асаалттай",
    notifOff: "Мэдэгдэл хаалттай (browser тохиргооноос асаана)",
    notifEnable: "Мэдэгдэл асаах",
    backToTeam: "← Бүх баг",
    directChat: "шууд чат",
    suggestions: [
      "unegui.mn-ээс 2 өрөө байрны түрээс хайгаач",
      "Энэ сарын маркетингийн санаа гаргаач",
      "Шинэ ажилтан авах зар бичээч",
    ],
    // landing
    navFeatures: "Боломжууд",
    navHow: "Хэрхэн ажилладаг",
    navContact: "Холбоо барих",
    liveDemo: "Демо",
    signIn: "Чатлах",
    heroBadge: "Таны AI ажиллах хүч, онлайн",
    heroTitlePre: "Автономит",
    heroTitleAccent: "агент багуудын платформ",
    heroSub:
      "Agentic Force нь AI агентуудыг бүтээж, удирдаж, бодит цагт хамтран ажиллахыг харах боломж олгоно. Хүнд ажлуудаа агентуудын багт даатгаарай.",
    heroCtaLogin: "Туслахтай чатлах",
    heroCtaDemo: "Демо үзэх",
  },
  en: {
    assistantSubtitle: "Agentic Force assistant",
    signOut: "Sign out",
    greeting: "Hello",
    greetingSub: "I'm Saruul — tell me your task and the team will handle it.",
    placeholder: "Message Saruul…",
    thinking: "Saruul is thinking…",
    working: "The team is working…",
    searching: "searching",
    errorPrefix: "Error",
    networkError: "Network error.",
    notifOn: "Notifications on",
    notifOff: "Notifications blocked (enable in browser settings)",
    notifEnable: "Enable notifications",
    backToTeam: "← Whole team",
    directChat: "direct chat",
    suggestions: [
      "Find 2-room apartment rentals on unegui.mn",
      "Give me marketing ideas for this month",
      "Write a job posting for a new hire",
    ],
    navFeatures: "Features",
    navHow: "How it works",
    navContact: "Contact",
    liveDemo: "Live Demo",
    signIn: "Open chat",
    heroBadge: "Your AI workforce, online and working",
    heroTitlePre: "The platform for",
    heroTitleAccent: "autonomous agent teams",
    heroSub:
      "Agentic Force lets you build, orchestrate and watch AI agents collaborate in real time. Turn your toughest workflows over to a team of agents.",
    heroCtaLogin: "Try the assistant",
    heroCtaDemo: "Watch the Demo",
  },
  zh: {
    assistantSubtitle: "Agentic Force 助手",
    signOut: "退出",
    greeting: "你好",
    greetingSub: "我是 Saruul — 告诉我你的任务，团队会协作完成。",
    placeholder: "给 Saruul 发消息…",
    thinking: "Saruul 正在思考…",
    working: "团队正在工作…",
    searching: "正在搜索",
    errorPrefix: "错误",
    networkError: "网络错误。",
    notifOn: "通知已开启",
    notifOff: "通知已关闭（请在浏览器设置中开启）",
    notifEnable: "开启通知",
    backToTeam: "← 整个团队",
    directChat: "直接聊天",
    suggestions: [
      "在 unegui.mn 上查找两居室租房",
      "给我这个月的营销点子",
      "为新职位写一份招聘启事",
    ],
    navFeatures: "功能",
    navHow: "工作原理",
    navContact: "联系",
    liveDemo: "演示",
    signIn: "开始聊天",
    heroBadge: "你的 AI 团队，随时在线",
    heroTitlePre: "打造你的",
    heroTitleAccent: "自主智能体团队",
    heroSub:
      "Agentic Force 让你构建、编排并实时观看 AI 智能体协作。把最棘手的工作交给智能体团队。",
    heroCtaLogin: "与助手聊天",
    heroCtaDemo: "观看演示",
  },
};

const STORAGE_KEY = "af-lang";

function parseLang(v: string | null): Lang {
  // Mongolian is the default; only an explicit "en" / "zh" overrides it.
  return v === "en" || v === "zh" ? v : "mn";
}

export function getStoredLang(): Lang {
  if (typeof window === "undefined") return "mn";
  try {
    return parseLang(localStorage.getItem(STORAGE_KEY));
  } catch {
    return "mn";
  }
}

/** Reactive language state synced across components via a storage event. */
export function useLang(): { lang: Lang; t: Dict; setLang: (l: Lang) => void } {
  const [lang, setLangState] = useState<Lang>("mn");

  useEffect(() => {
    setLangState(getStoredLang());
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setLangState(parseLang(e.newValue));
    };
    const onCustom = () => setLangState(getStoredLang());
    window.addEventListener("storage", onStorage);
    window.addEventListener("af-lang-change", onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("af-lang-change", onCustom);
    };
  }, []);

  const setLang = (l: Lang) => {
    try {
      localStorage.setItem(STORAGE_KEY, l);
      document.cookie = `af-lang=${l}; path=/; max-age=31536000; samesite=lax`;
    } catch {}
    setLangState(l);
    window.dispatchEvent(new Event("af-lang-change"));
  };

  return { lang, t: DICT[lang], setLang };
}
